import { useAppDispatch, useAppSelector } from 'app/hooks';
import { SizingItemsType } from 'data/types';
import {
  markDefaultCategoryModified,
  updateDefaultSizingItemCategory
} from 'features/data/data-slice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import {
  InsulationLevelNumber,
  matchDualValueLevel,
  matchSingleValueLevel,
  matchWallLevel,
  ParsedInsulationLevelMaps,
  parseInsulationLevelSettings
} from 'utils/insulation-level-mappings';
import { getHasAttic } from 'utils/structure-basics';
import { getStructureSizingPresence } from 'utils/structure-sizing-presence';

export type InsulationLevel = InsulationLevelNumber | 'Custom';

export type InsulationSliderRow = {
  key: 'walls' | 'ceilingBelowRoof' | 'attic' | 'floor';
  label: string;
  tooltip: string;
  value: InsulationLevel;
  onChange: (level: InsulationLevelNumber) => void;
  suggestedLevel: InsulationLevelNumber | undefined;
  levelTooltips: Record<InsulationLevelNumber, string> | undefined;
  disabledReason: string | undefined;
};

type SuggestedLevels = {
  walls: InsulationLevelNumber;
  attic: InsulationLevelNumber;
  ceilingBelowRoof: InsulationLevelNumber;
  floor: InsulationLevelNumber;
} | null;

const ALL_LEVELS: InsulationLevelNumber[] = [1, 2, 3, 4, 5];

function buildLevelTooltipMaps(levelMaps: ParsedInsulationLevelMaps) {
  const buildMap = (fn: (lvl: InsulationLevelNumber) => string) =>
    Object.fromEntries(ALL_LEVELS.map((lvl) => [lvl, fn(lvl)])) as Record<
      InsulationLevelNumber,
      string
    >;

  return {
    walls: buildMap((lvl) => {
      const m = levelMaps.walls[lvl];
      return m
        ? `Cavity: ${m.cavityInsulation}, Board: ${m.boardInsulation}`
        : '';
    }),
    attic: buildMap((lvl) => {
      const m = levelMaps.attic[lvl];
      return m ? m.insulationValue : '';
    }),
    ceilingBelowRoof: buildMap((lvl) => {
      const m = levelMaps.ceilingBelowRoof[lvl];
      return m ? `Joists: ${m.primary}, Beams: ${m.secondary}` : '';
    }),
    floor: buildMap((lvl) => {
      const m = levelMaps.floor[lvl];
      return m
        ? `Over basement: ${m.primary}, Over garage: ${m.secondary}`
        : '';
    })
  };
}

export function useInsulationSliderRows(): InsulationSliderRow[] {
  const dispatch = useAppDispatch();
  const { defaultSizingItems, insulationLevelSettings } = useAppSelector(
    (state) => state.data
  );
  const { projectSettings, attomData, locationData } = useAppSelector(
    (state) => state.project
  );
  const { structure } = useAppSelector((state) => state.structure);

  const yearBuilt =
    projectSettings.find((ps) => ps.code === 'year_built')?.value ||
    attomData?.summary?.yearBuilt;

  const [suggestedLevels, setSuggestedLevels] = useState<SuggestedLevels>(null);

  const insulationDisabledReasons = useMemo(() => {
    const presence = getStructureSizingPresence(structure);
    const hasRoofCeiling =
      presence.ceilingBelowRoofJoists || presence.ceilingOnExposedBeams;
    const hasFloors =
      presence.floorOverUnconditionedSpace || presence.floorOverExposedSpace;

    return {
      walls: presence.aboveGradeWall
        ? undefined
        : 'No wall sizing items exist on the structure',
      ceilingBelowRoof: hasRoofCeiling
        ? undefined
        : 'No ceiling below roof sizing items exist on the structure',
      attic: getHasAttic(structure)
        ? undefined
        : 'No attic exists on the structure',
      floor: hasFloors
        ? undefined
        : 'No unconditioned floor or exposed floor sizing items exist on the structure'
    };
  }, [structure]);

  const levelMaps: ParsedInsulationLevelMaps | null = useMemo(
    () =>
      insulationLevelSettings
        ? parseInsulationLevelSettings(insulationLevelSettings)
        : null,
    [insulationLevelSettings]
  );

  const levelTooltipMaps = useMemo(
    () => (levelMaps ? buildLevelTooltipMaps(levelMaps) : null),
    [levelMaps]
  );

  const fetchInsulationDefaults = useCallback(async () => {
    const climateZone = locationData?.climateZone || '4';
    if (!yearBuilt) return null;
    try {
      const response = await axios.get(
        `sizing/insulation-defaults?year=${yearBuilt}&climateZone=${climateZone}`
      );
      return response.data;
    } catch {
      return null;
    }
  }, [yearBuilt, locationData]);

  useEffect(() => {
    fetchInsulationDefaults().then((data) => {
      if (data?.suggestedLevels) {
        setSuggestedLevels(data.suggestedLevels);
      }
    });
  }, [fetchInsulationDefaults]);

  const wallValue: InsulationLevel = useMemo(() => {
    if (!levelMaps) return 'Custom';
    const board = defaultSizingItems.aboveGradeWall?.boardInsulation;
    const cavity = defaultSizingItems.aboveGradeWall?.cavityInsulation;
    return matchWallLevel(cavity || 'None', board || 'None', levelMaps.walls);
  }, [defaultSizingItems.aboveGradeWall, levelMaps]);

  const ceilingBelowRoofValue: InsulationLevel = useMemo(() => {
    if (!levelMaps) return 'Custom';
    const roofJoists =
      defaultSizingItems.ceilingBelowRoofJoists?.insulationValue;
    const exposedBeams =
      defaultSizingItems.ceilingOnExposedBeams?.insulationValue;
    return matchDualValueLevel(
      roofJoists || 'None',
      exposedBeams || 'None',
      levelMaps.ceilingBelowRoof
    );
  }, [
    defaultSizingItems.ceilingBelowRoofJoists,
    defaultSizingItems.ceilingOnExposedBeams,
    levelMaps
  ]);

  const atticValue: InsulationLevel = useMemo(() => {
    if (!levelMaps) return 'Custom';
    const value = defaultSizingItems.ceilingBelowAttic?.insulationValue;
    return matchSingleValueLevel(value || 'None', levelMaps.attic);
  }, [defaultSizingItems.ceilingBelowAttic, levelMaps]);

  const floorValue: InsulationLevel = useMemo(() => {
    if (!levelMaps) return 'Custom';
    const unconditioned =
      defaultSizingItems.floorOverUnconditionedSpace?.insulationValue;
    const exposed = defaultSizingItems.floorOverExposedSpace?.insulationValue;
    return matchDualValueLevel(
      unconditioned || 'None',
      exposed || 'None',
      levelMaps.floor
    );
  }, [
    defaultSizingItems.floorOverUnconditionedSpace,
    defaultSizingItems.floorOverExposedSpace,
    levelMaps
  ]);

  const handleWallChange = useCallback(
    (level: InsulationLevelNumber) => {
      if (!levelMaps) return;
      const { boardInsulation, cavityInsulation } = levelMaps.walls[level];
      dispatch(
        updateDefaultSizingItemCategory({
          category: 'aboveGradeWall',
          updates: {
            boardInsulation:
              boardInsulation as SizingItemsType['aboveGradeWall']['boardInsulation'],
            cavityInsulation:
              cavityInsulation as SizingItemsType['aboveGradeWall']['cavityInsulation']
          }
        })
      );
      dispatch(markDefaultCategoryModified('walls'));
    },
    [dispatch, levelMaps]
  );

  const handleCeilingBelowRoofChange = useCallback(
    (level: InsulationLevelNumber) => {
      if (!levelMaps) return;
      const { primary, secondary } = levelMaps.ceilingBelowRoof[level];
      dispatch(
        updateDefaultSizingItemCategory({
          category: 'ceilingBelowRoofJoists',
          updates: {
            insulationValue:
              primary as SizingItemsType['ceilingBelowRoofJoists']['insulationValue']
          }
        })
      );
      dispatch(
        updateDefaultSizingItemCategory({
          category: 'ceilingOnExposedBeams',
          updates: {
            insulationValue:
              secondary as SizingItemsType['ceilingOnExposedBeams']['insulationValue']
          }
        })
      );
      dispatch(markDefaultCategoryModified('ceilings'));
    },
    [dispatch, levelMaps]
  );

  const handleAtticChange = useCallback(
    (level: InsulationLevelNumber) => {
      if (!levelMaps) return;
      const { insulationValue } = levelMaps.attic[level];
      dispatch(
        updateDefaultSizingItemCategory({
          category: 'ceilingBelowAttic',
          updates: {
            insulationValue:
              insulationValue as SizingItemsType['ceilingBelowAttic']['insulationValue']
          }
        })
      );
      dispatch(markDefaultCategoryModified('ceilings'));
    },
    [dispatch, levelMaps]
  );

  const handleFloorChange = useCallback(
    (level: InsulationLevelNumber) => {
      if (!levelMaps) return;
      const { primary, secondary } = levelMaps.floor[level];
      dispatch(
        updateDefaultSizingItemCategory({
          category: 'floorOverUnconditionedSpace',
          updates: {
            insulationValue:
              primary as SizingItemsType['floorOverUnconditionedSpace']['insulationValue']
          }
        })
      );
      dispatch(
        updateDefaultSizingItemCategory({
          category: 'floorOverExposedSpace',
          updates: {
            insulationValue:
              secondary as SizingItemsType['floorOverExposedSpace']['insulationValue']
          }
        })
      );
      dispatch(markDefaultCategoryModified('floors'));
    },
    [dispatch, levelMaps]
  );

  return useMemo(
    () => [
      {
        key: 'walls',
        label: 'Wall insulation',
        tooltip:
          'Higher rated insulation within walls reduce heat loss/gain through exterior walls.',
        value: wallValue,
        onChange: handleWallChange,
        suggestedLevel: suggestedLevels?.walls,
        levelTooltips: levelTooltipMaps?.walls,
        disabledReason: insulationDisabledReasons.walls
      },
      {
        key: 'ceilingBelowRoof',
        label: 'Ceiling below roof insulation',
        tooltip:
          'Higher rated insulation within ceilings (not below attic) reduce heat loss/gain through the ceiling.',
        value: ceilingBelowRoofValue,
        onChange: handleCeilingBelowRoofChange,
        suggestedLevel: suggestedLevels?.ceilingBelowRoof,
        levelTooltips: levelTooltipMaps?.ceilingBelowRoof,
        disabledReason: insulationDisabledReasons.ceilingBelowRoof
      },
      {
        key: 'attic',
        label: 'Attic insulation',
        tooltip:
          'Higher rated insulation in an attic reduces heat loss/gain through the ceiling (below attic).',
        value: atticValue,
        onChange: handleAtticChange,
        suggestedLevel: suggestedLevels?.attic,
        levelTooltips: levelTooltipMaps?.attic,
        disabledReason: insulationDisabledReasons.attic
      },
      {
        key: 'floor',
        label: 'Floor over basement/garage insulation',
        tooltip:
          'Higher rated insulation in floors over unconditioned or exposed spaces reduces heat loss/gain through their respective floors.',
        value: floorValue,
        onChange: handleFloorChange,
        suggestedLevel: suggestedLevels?.floor,
        levelTooltips: levelTooltipMaps?.floor,
        disabledReason: insulationDisabledReasons.floor
      }
    ],
    [
      wallValue,
      ceilingBelowRoofValue,
      atticValue,
      floorValue,
      handleWallChange,
      handleCeilingBelowRoofChange,
      handleAtticChange,
      handleFloorChange,
      suggestedLevels,
      levelTooltipMaps,
      insulationDisabledReasons
    ]
  );
}
